import { Fragment, useEffect, useRef, useState } from "react";
import {
    PiGitPullRequest,
    PiMagnifyingGlassBold,
    PiXBold,
} from "react-icons/pi";
import { Link } from "react-router-dom";
import {
    ActionIcon,
    Badge,
    Button,
    Empty,
    Input,
    SearchNotFoundIcon,
} from "rizzui";
import { Order, OrderStatus } from "@/types";
import { fetchOrderByTrackingNumber } from "@/server/api-services";
import routes from "@/routers/routes";

export default function SearchList({ onClose }: { onClose?: () => void }) {
    const inputRef = useRef(null);
    const [searchText, setSearchText] = useState("");

    const [orders, setOrders] = useState<Order[]>([]);

    async function loadOrders(trackingNumber: string) {
        setSearchText(trackingNumber);

        if(trackingNumber.length === 0) {
            setOrders([]);
            return;
        }

        const response = await fetchOrderByTrackingNumber(trackingNumber);

        if (response.statusCode !== 200) {
            return;
        }

        var data = response.data as Order[];

        // Map string status to OrderStatus enum
        data = data.map((order) => ({
            ...order,
            status: OrderStatus[order.status as keyof typeof OrderStatus],
        }));

        console.log("fetchOrders", data);
        setOrders(data);
    }

    let menuItemsFiltered = orders;
    if (searchText.length > 0) {
        menuItemsFiltered = orders.filter((item: Order) => {
            const orderId = item?.orderId;
            const productName = item?.product?.name?.toLowerCase();
            const categoryName = item?.product?.category?.name?.toLowerCase();
            const trackingNumber = item?.trackingNumber?.toLowerCase();
            const searchTextLower = searchText.toLowerCase();
            return (
                productName?.includes(searchTextLower) ||
                categoryName?.includes(searchTextLower) ||
                trackingNumber?.includes(searchTextLower) ||
                orderId?.includes(searchText)
            );
        });
    }

    useEffect(() => {
        if (inputRef?.current) {
            // @ts-ignore
            inputRef.current.focus();
        }
        return () => {
            inputRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div className="flex items-center px-5 py-4">
                <Input
                    variant="flat"
                    value={searchText}
                    ref={inputRef}
                    onChange={(e) => loadOrders(e.target.value)}
                    placeholder="Search pages here"
                    className="flex-1"
                    prefix={
                        <PiMagnifyingGlassBold className="h-[18px] w-[18px] text-gray-600" />
                    }
                    suffix={
                        searchText && (
                            <Button
                                size="sm"
                                variant="text"
                                className="h-auto w-auto px-0"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setSearchText(() => "");
                                }}
                            >
                                Clear
                            </Button>
                        )
                    }
                />
                <ActionIcon
                    variant="text"
                    size="sm"
                    className="ms-3 text-gray-500 hover:text-gray-700"
                    onClick={onClose}
                >
                    <PiXBold className="h-5 w-5" />
                </ActionIcon>
            </div>

            <div className="custom-scrollbar max-h-[60vh] overflow-y-auto border-t border-gray-300 px-2 py-4">
                <>
                    {menuItemsFiltered.length === 0 ? (
                        <Empty
                            className="scale-75"
                            image={<SearchNotFoundIcon />}
                            text="No Result Found"
                            textClassName="text-xl"
                        />
                    ) : null}
                </>

                {menuItemsFiltered.map((item, index) => {
                    return (
                        <Fragment key={item?.product?.name + "-" + index}>
                            <Link
                                to={routes.orders.tracking(item.id)}
                                className="relative my-0.5 flex items-center rounded-lg px-3 py-2 text-sm hover:bg-gray-100 focus:outline-none focus-visible:bg-gray-100 dark:hover:bg-gray-50/50 dark:hover:backdrop-blur-lg"
                            >
                                <span className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 text-gray-500">
                                    <PiGitPullRequest className="h-5 w-5" />
                                </span>

                                <span className="ms-3 grid gap-0.5">
                                    <span className="font-medium capitalize text-gray-900 dark:text-gray-700">
                                        {item?.product?.name}
                                    </span>
                                    <span className="text-gray-500">
                                        {item?.trackingNumber} {" "}|{" "}
                                        {item?.product?.category?.name} {" "}|{" "}
                                        {item?.status ===
                                        OrderStatus.Delivered ? (
                                            <Badge color="success" size="md" variant="flat"  rounded="md">
                                                {item?.status}
                                            </Badge>
                                        ) : item?.status ===
                                          OrderStatus.Requested ? (
                                            <Badge color="warning" size="md"  variant="flat"  rounded="md">
                                                {item?.status}
                                            </Badge>
                                        ) : item?.status ===
                                          OrderStatus.Cancelled ? (
                                            <Badge color="danger" size="md" variant="flat" rounded="md">
                                                {item?.status}
                                            </Badge>
                                        ) : item?.status ===
                                          OrderStatus.Processing ? (
                                            <Badge
                                                color="secondary" size="md"  variant="flat" 
                                                rounded="md"
                                            >
                                                {item?.status}
                                            </Badge>
                                        ) : item?.status ===
                                          OrderStatus.Purchased ? (
                                            <Badge color="primary" size="md" variant="flat" rounded="md">
                                                {item?.status}
                                            </Badge>
                                        ) : item?.status ===
                                          OrderStatus.InTransit ? (
                                            <Badge color="info" size="md"  variant="flat" rounded="md">
                                                {item?.status}
                                            </Badge>
                                        ) : null}
                                    </span>
                                </span>
                            </Link>
                        </Fragment>
                    );
                })}
            </div>
        </>
    );
}
